import BigNumber from "bignumber.js";
import Bowser from "bowser";
import copyToClipboard from "copy-to-clipboard";
import { convertStringToHex } from "xrpl";

import config from "@/config";
import { addToast } from "@/modules/app";

import { LOCALE_EN, LOGIN_CONFIG, STORAGE_TYPE } from "./enums";
import { IToken } from "./interfaces";

export function getStorage(key: STORAGE_TYPE): Storage | undefined {
  if (config.isStorageAvailable[key]) return window[key];
  return undefined;
}

const browser = Bowser.getParser(window.navigator.userAgent);
export const isDesktop = browser.getPlatform().type === "desktop";
console.log("browser.getPlatform().type", isDesktop); // eslint-disable-line no-console
export const isMain = window.self === window.top;

export const csprToMotes = (cspr: number): number => cspr * 10 ** 9;

export const tagPublicKey = (rawPublicKey: string): string => `02${rawPublicKey}`;

export const randomNumericId = (): number => Math.floor(Math.random() * 1000000000);

export const copyText = (text: string, label?: string): void => {
  copyToClipboard(text);
  addToast({ message: `Copied${label ? ` ${label}` : ""}`, type: "success" });
};

export const supportedCurrencies = (ticker: string): string[] => {
  const returnArr = ["CSPR", ...config.supportedCurrencies];
  if (ticker !== "CSPR") returnArr.unshift(ticker);
  return returnArr;
};

export const openLinkNewTab = (link: string): void => {
  window.open(link, "_blank");
};

export const getUserLanguage = (): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let userLanguage = (window.navigator as any).userLanguage || window.navigator.language || "en-US";
  userLanguage = userLanguage.split("-");
  userLanguage = userLanguage[0] || LOCALE_EN;
  return userLanguage;
};

export function promiseCreator<T>(): {
  resolve: ((value: T | PromiseLike<T>) => void) | null;
  reject: ((reason?: string) => void) | null;
  promise: Promise<T>;
} {
  let res: ((value: T | PromiseLike<T>) => void) | null = null;
  let rej: ((reason?: string) => void) | null = null;
  const promise = new Promise<T>((resolve, reject) => {
    res = resolve;
    rej = reject;
  });
  return {
    resolve: res,
    reject: rej,
    promise,
  };
}

export const normalizeJson = <T>(json: unknown): T => {
  return JSON.parse(JSON.stringify(json));
};
export function capitalizeFirstLetter(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function thirdPartyAuthenticators(loginButtons: LOGIN_CONFIG[]): string {
  const finalAuthenticators: string[] = loginButtons
    .reduce((authenticators: string[], authenticator) => {
      if (Object.prototype.hasOwnProperty.call(authenticator, "jwtParameters")) {
        authenticators.push(capitalizeFirstLetter(authenticator.name));
      }
      return authenticators;
    }, [])
    .sort((a, b) => {
      if (a > b) return 1;
      if (a < b) return -1;
      return 0;
    });

  return finalAuthenticators.join(", ");
}

export const getIFrameOrigin = () => {
  const originHref = window.location.ancestorOrigins?.length > 0 ? window.location.ancestorOrigins[0] : document.referrer;
  if (!originHref) return originHref;
  const url = new URL(originHref);
  return url.origin;
};

export const getIFrameOriginObject = () => {
  try {
    // eslint-disable-next-line no-console
    console.log("getIFrameOrigin()", getIFrameOrigin());
    const url = new URL(getIFrameOrigin());
    return { href: url.href, hostname: url.hostname };
  } catch {
    return { href: window.location.href, hostname: window.location.hostname };
  }
};

export const storageUtils = {
  storage: () => {
    if (config.isCustomLogin === null) {
      if (config.storageAvailability.session) {
        return window.sessionStorage;
      }
      return undefined;
    }
    if (config.storageAvailability.local) {
      return window.localStorage;
    }
    return undefined;
  },
  storageType: config.isCustomLogin === null ? "session" : "local",
  storageKey: config.isCustomLogin() === true ? `xrpl_app_${config.namespace || getIFrameOriginObject().hostname}` : "xrpl_app",
  openloginStoreKey: config.isCustomLogin() === true ? `openlogin_store_${config.namespace || getIFrameOriginObject().hostname}` : "openlogin_store",
};

export const getSessionIdFromStorage = () => {
  const storage = storageUtils.storage();
  if (!storage) return "";
  const sessionData = storage.getItem(`${storageUtils.openloginStoreKey}`) || "{}";
  return JSON.parse(sessionData).sessionId || "";
};

export function formatDate(inputDate: string) {
  const monthList = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(inputDate);
  const day = date.getDate();
  const month = monthList[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export function formatTime(time: string) {
  return new Date(time).toTimeString().slice(0, 8);
}

export function trimAddress(address: string, sliceLeft = 7, sliceRight = 6) {
  // XRPL address is between 25 and 35 characters in length
  if (!address || address.length < 25) return address;

  const first = address.slice(0, sliceLeft);
  const last = address.slice(-sliceRight);
  return `${first}...${last}`;
}

export const setAccountActivationHintStatus = () => {
  if (config.isStorageAvailable.sessionStorage) {
    sessionStorage.setItem("accountActivationHinted", "true");
  }
};

export const removeAccountActivationHintStatus = () => {
  if (config.isStorageAvailable.sessionStorage) {
    sessionStorage.removeItem("accountActivationHinted");
  }
};

export const getAccountActivationHintStatus = () => {
  if (config.isStorageAvailable.sessionStorage) {
    return !!sessionStorage.getItem("accountActivationHinted");
  }
  return false;
};

export const setTheme = (isDarkMode = false) => {
  const htmlElement = document.querySelector("html") as HTMLElement;

  if (htmlElement) {
    if (isDarkMode) htmlElement.classList.add("dark");
    else htmlElement.classList.remove("dark");
  }
};

export const currencyToHex40 = (currency: string) => {
  let hex = convertStringToHex(currency);
  while (hex.length < 40) {
    hex += "0";
  }
  return hex.toUpperCase();
};

export const parseJwt = (token: string) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    Buffer.from(base64, "base64")
      .toString()
      .split("")
      .map((c) => {
        return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

export const genXrpToken = (price: BigNumber | string, amount: string, currency: string): IToken => {
  const bnPrice = new BigNumber(price);
  return {
    name: "Ripple",
    symbol: "XRP",
    price: bnPrice.toFormat(2),
    amount: `${amount} XRP`,
    amountConverted: `${bnPrice.multipliedBy(amount).toFormat(2)} ${currency}`,
    logo: "https://xpcdn.xpmarket.com/gravatars/fixed/Xrp.png",
    issuerName: "",
    issuerAddress: "",
    issuerFee: 0,
  };
};
